import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipProvider } from "@/components/ui/tooltip";
import AccessibilitySettingsPopup from "@/components/AccessibilitySettingsPopup";
import { useToast } from "@/hooks/use-toast";
import useAppLinks from "@/lib/hooks/useAppLinks";
import useUser from "@/lib/queries/user/useUser";
import { cn } from "@/lib/utils";
import { GraduationCap, List, SignIn, UserCircle } from "@phosphor-icons/react";
import { useStytch, useStytchSession } from "@stytch/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  ROOT,
  AUTHENTICATE,
  SETTINGS,
  SETTINGS_SUBSCRIPTION,
  SETTINGS_INVOICES,
} from "@/lib/links";

function MobileNav() {
  const stytchSession = useStytchSession();
  const { data: user } = useUser({
    enabled: stytchSession.session !== null,
  });
  const stytch = useStytch();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const logout = () => {
    stytch.session.revoke().then(() => {
      toast.toast({
        title: "Logged out",
        description: "You have been logged out.",
      });
      queryClient.removeQueries({
        queryKey: ["user"],
      });
      queryClient.invalidateQueries({
        queryKey: ["init"],
      });
      queryClient.refetchQueries({
        queryKey: ["init"],
      });

      navigate(ROOT);

      window.sessionStorage.clear();
      window.localStorage.removeItem("tchr_client_key");
    });
  };
  const { tools: appLinks } = useAppLinks();

  if (!stytchSession.session) {
    return (
      <nav className="sticky top-0 z-50 block p-4 shadow-md bg-card">
        <TooltipProvider delayDuration={100}>
          <div className="flex flex-row justify-between w-full gap-4">
            <Link to={ROOT} className="text-2xl font-semibold">
              <GraduationCap className="w-8 h-8" weight="bold" />
            </Link>
            <div className="flex flex-row items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-foreground"
                  >
                    <List className="w-8 h-8" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="flex flex-col gap-2 py-4 mr-2"
                >
                  {appLinks.map((link) => (
                    <DropdownMenuItem asChild key={link.href}>
                      <NavLink
                        to={link.href}
                        className="inline-block w-full h-10"
                        onClick={(e) =>
                          !link.active ? e.preventDefault() : null
                        }
                      >
                        {({ isActive }) => (
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={!link.active}
                            className={cn(
                              isActive
                                ? "bg-secondary text-green-950"
                                : "text-foreground",
                              "p-1 hover:bg-secondary hover:text-green-950 h-fit w-full flex flex-row justify-start gap-2"
                            )}
                          >
                            {link.icon ? (
                              <link.icon className="!w-8 !h-8" />
                            ) : (
                              link.title
                            )}

                            {link.title}
                          </Button>
                        )}
                      </NavLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <AccessibilitySettingsPopup />
              <Link to={AUTHENTICATE} className="w-8 h-8">
                <SignIn className="w-8 h-8" />
              </Link>
            </div>
          </div>
        </TooltipProvider>
      </nav>
    );
  }

  return (
    <nav className="sticky top-0 z-50 block p-4 shadow-md bg-card">
      <TooltipProvider delayDuration={100}>
        <div className="flex flex-row justify-between w-full gap-4">
          <Link to={ROOT} className="text-2xl font-semibold">
            <GraduationCap className="w-8 h-8" weight="bold" />
          </Link>
          <div className="flex flex-row gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button size="icon" variant="ghost" className="text-foreground">
                  <List className="w-8 h-8" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="flex flex-col gap-2 mr-2"
              >
                {appLinks
                  .filter((l) => l.active)
                  .map((link) => (
                    <DropdownMenuItem asChild key={link.href}>
                      <NavLink
                        to={link.href}
                        className="inline-block w-full h-10"
                        onClick={(e) =>
                          !link.active ? e.preventDefault() : null
                        }
                      >
                        {({ isActive }) => (
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={!link.active}
                            className={cn(
                              isActive
                                ? "bg-secondary text-green-950"
                                : "text-foreground",
                              "p-1 hover:bg-secondary hover:text-green-950 h-fit w-full flex flex-row justify-start gap-2"
                            )}
                          >
                            {link.icon ? (
                              <link.icon className="!w-8 !h-8" />
                            ) : (
                              link.title
                            )}

                            {link.title}
                          </Button>
                        )}
                      </NavLink>
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <AccessibilitySettingsPopup />
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Avatar>
                  {user?.settings.firstName && user?.settings.lastName ? (
                    <AvatarFallback>
                      {user?.settings.firstName[0].toUpperCase() +
                        user?.settings.lastName[0].toUpperCase()}
                    </AvatarFallback>
                  ) : (
                    <UserCircle className="w-full h-full" />
                  )}
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>
                  {user?.settings.firstName
                    ? `${user?.settings.firstName} ${user?.settings.lastName}`
                    : "Welcome"}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Settings</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <NavLink to={SETTINGS}>Profile</NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NavLink to={SETTINGS_SUBSCRIPTION}>Subscription</NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NavLink to={SETTINGS_INVOICES}>My Invoices</NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Button
                    variant="ghost"
                    onClick={logout}
                    className="justify-start w-full"
                  >
                    Logout
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </TooltipProvider>
    </nav>
  );
}

export default MobileNav;
